import React, { useState } from 'react';
import { ExternalLink, Book, Clock, Download } from 'lucide-react';
import ReviewSubmissionModal from './ReviewSubmissionModal';
import { downloadFileFromFirestore, triggerFileDownload } from './chunked-firestore-storage'; // 🔧 NEW

const ClaimedBooksWorkflow = ({ 
  claimedBooks, 
  currentUser, 
  books,
  bookProgress,
  setView,
  setReviewForm,
  updateBookProgress,
  setBookProgress,
  adaptAmazonLink,
  getMarketplaceInfo,
  handleSubmitReview,
  AMAZON_MARKETPLACES
}) => {
  const [bookSteps, setBookSteps] = useState({});
  const [expandedBook, setExpandedBook] = useState(null);
  const [selectedBookForReview, setSelectedBookForReview] = useState(null);
  const [downloading, setDownloading] = useState(false); // 🔧 NEW: Track download state

  const toggleStep = (bookId, step) => {
    setBookSteps(prev => ({
      ...prev,
      [bookId]: {
        ...prev[bookId],
        [step]: !prev[bookId]?.[step]
      }
    }));
  };

  const calculateDeadline = (pageCount) => {
    const daysToRead = pageCount < 200 ? 2 : pageCount < 350 ? 3 : 4;
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + daysToRead);
    return deadline.toLocaleDateString();
  };

  const getReaderTypeInfo = (book) => {
    if (book.readerTypes?.verifiedPurchase) {
      return {
        type: "Verified Purchase",
        color: "bg-green-50 border-green-200 text-green-800",
        instruction: "Please use the free promotion in order to purchase the ebook directly from Amazon."
      };
    } else if (book.readerTypes?.kindleUnlimited) {
      return {
        type: "Kindle Unlimited",
        color: "bg-orange-50 border-orange-200 text-orange-800",
        instruction: "Access this book using your Kindle Unlimited subscription."
      };
    } else {
      return {
        type: "Standard",
        color: "bg-blue-50 border-blue-200 text-blue-800",
        instruction: "Download the book file provided by the author below."
      };
    }
  };

  // 🔧 NEW: Download book from chunked Firestore storage
  const handleDownloadBook = async (book) => {
    if (!book.hasBookFile) {
      alert('❌ No book file available for download');
      return;
    }

    try {
      setDownloading(true);
      console.log('📥 Downloading book:', book.title);
      
      // Download and reassemble file from Firestore chunks
      const { base64Data, fileName, fileType } = await downloadFileFromFirestore(book.id);
      
      // Trigger browser download
      triggerFileDownload(base64Data, fileName, fileType);
      
      setDownloading(false);
      console.log('✅ Download complete!');
    } catch (error) {
      setDownloading(false);
      console.error('Download error:', error);
      alert('❌ Download failed: ' + error.message + '\n\nPlease try again or contact support.');
    }
  };

  const getUserClaimedBooks = () => {
    if (!claimedBooks || !Array.isArray(claimedBooks)) return [];
    if (!books || !Array.isArray(books)) return [];
    if (!currentUser?.id) return [];
    
    return claimedBooks
      .filter(cb => cb.userId === currentUser.id)
      .map(cb => books.find(b => b.id === cb.bookId))
      .filter(book => book);
  };

  const claimedBooksList = getUserClaimedBooks();

  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Books You're Reviewing</h2>

      {claimedBooksList.length === 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <Book className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600 text-lg mb-4">No claimed books yet.</p>
          <button 
            onClick={() => setView('home')}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition"
          >
            Browse Books
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          {claimedBooksList.map(book => {
            const steps = bookSteps[book.id] || {};
            const readerType = getReaderTypeInfo(book);
            const isExpanded = expandedBook === book.id;
            const progress = bookProgress.find(bp => bp.bookId === book.id && bp.reviewerId === currentUser.id);

            return (
              <div key={book.id} className="bg-white rounded-xl shadow-lg overflow-hidden">
                {/* Book Header */}
                <div className="p-6 border-b bg-gradient-to-r from-indigo-50 to-purple-50">
                  <div className="flex gap-4">
                    {book.coverImage && (
                      <img 
                        src={book.coverImage} 
                        alt={book.title}
                        className="w-24 h-36 object-cover rounded-lg shadow"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    )}
                    <div className="flex-1">
                      <h3 className="text-2xl font-bold text-gray-800 mb-1">{book.title}</h3>
                      <p className="text-gray-600 mb-3">by {book.author}</p>
                      <div className="flex items-center gap-4 text-sm flex-wrap">
                        <span className={`px-3 py-1 rounded-full border ${readerType.color} font-medium`}>
                          {readerType.type}
                        </span>
                        <span className="flex items-center gap-1 text-amber-700">
                          <span className="text-lg">🪙</span>
                          <span className="font-bold">Earn {book.coinsRequired} coins</span>
                        </span>
                        <span className="flex items-center gap-1 text-gray-600">
                          <Clock className="w-4 h-4" />
                          Due: {calculateDeadline(book.pageCount)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* The Main Steps */}
                <div className="p-6">
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-xl p-4 mb-6">
                    <h4 className="text-lg font-bold text-purple-900 mb-3">The Main Steps!</h4>
                    <div className="space-y-1 text-gray-700">
                      <p className={steps.step1 ? 'line-through text-gray-400' : ''}>Step 1 - Access the book</p>
                      <p className={steps.step2 ? 'line-through text-gray-400' : ''}>Step 2 - Read the book</p>
                      <p className={steps.step3 ? 'line-through text-gray-400' : ''}>Step 3 - Post your review on Amazon</p>
                      <p className={steps.step4 ? 'line-through text-gray-400' : ''}>Step 4 - Submit your review proof</p>
                    </div>
                  </div>

                  {/* Step 1: Access the book */}
                  <div className="mb-6 border-2 border-gray-200 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={steps.step1}
                        onChange={() => toggleStep(book.id, 'step1')}
                        className="w-6 h-6 mt-1 text-indigo-600 rounded"
                      />
                      <div className="flex-1">
                        <h5 className={`text-lg font-bold mb-2 ${steps.step1 ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                          Access the book
                        </h5>
                        {!steps.step1 && (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-700">
                              This is a <strong>{readerType.type}</strong> assignment.
                            </p>
                            <p className="text-sm text-gray-700">
                              {readerType.instruction}
                            </p>
                            
                            {/* 🔧 FIXED: Download button checks for hasBookFile flag */}
                            {book.readerTypes?.standard && book.hasBookFile && (
                              <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="font-semibold text-blue-900 mb-1">📚 Book File Available</p>
                                    <p className="text-sm text-blue-700">
                                      {book.bookFileName || 'Download the book to read on your device'}
                                    </p>
                                   
                                  </div>
                                  <button
                                    onClick={() => handleDownloadBook(book)}
                                    disabled={downloading}
                                    className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium inline-flex items-center gap-2 disabled:bg-blue-400 disabled:cursor-not-allowed"
                                  >
                                    {downloading ? (
                                      <>
                                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                        <span>DOWNLOADING...</span>
                                      </>
                                    ) : (
                                      <>
                                        <Download className="w-5 h-5" />
                                        DOWNLOAD BOOK
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}

                            {/* 🔧 NEW: Show message if Standard is selected but no file */}
                            {book.readerTypes?.standard && !book.hasBookFile && (
                              <div className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-4">
                                <p className="font-semibold text-yellow-900 mb-1">⚠️ Book File Not Available</p>
                                <p className="text-sm text-yellow-700">
                                  The author hasn't uploaded a book file yet. Please contact support.
                                </p>
                              </div>
                            )}

                            <p className="text-sm text-gray-600">
                              Please do not purchase the paperback version of the book, as you will not have enough time to complete the assignment due to shipping delays.
                            </p>
                            
                            <div className="flex gap-3 flex-wrap">
                              <a
                                href={adaptAmazonLink ? adaptAmazonLink(book.amazonLink, currentUser?.country) : book.amazonLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition font-medium inline-flex items-center gap-2"
                              >
                                VIEW ON AMAZON
                                <ExternalLink className="w-4 h-4" />
                              </a>
                              <button className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition font-medium">
                                REPORT AN ISSUE
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step 2: Read the book */}
                  <div className="mb-6 border-2 border-gray-200 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={steps.step2}
                        onChange={async () => {
                          toggleStep(book.id, 'step2');
                          
                          if (progress?.id && !steps.step2) {
                            await updateBookProgress(progress.id, {
                              stage: 'reading',
                              startedReading: new Date().toISOString()
                            });
                            
                            setBookProgress(prev => prev.map(bp => 
                              bp.id === progress.id 
                                ? { ...bp, stage: 'reading', startedReading: new Date().toISOString() } 
                                : bp
                            ));
                          }
                        }}
                        className="w-6 h-6 mt-1 text-indigo-600 rounded"
                        disabled={!steps.step1}
                      />
                      <div className="flex-1">
                        <h5 className={`text-lg font-bold mb-2 ${steps.step2 ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                          Read the book
                        </h5>
                        {!steps.step2 && steps.step1 && (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-700">
                              Enjoy reading this book! To keep your account in good standing with both Amazon and Vibely, it's important to write thoughtful, opinionated, and lengthy reviews.
                            </p>
                            <p className="text-sm text-gray-700">
                              Remember, your honest opinion is welcome here! Take your time forming an opinion.
                            </p>
                            <button className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition font-medium">
                              REPORT AN ISSUE
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step 3: Post your review on Amazon */}
                  <div className="mb-6 border-2 border-gray-200 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={steps.step3}
                        onChange={() => toggleStep(book.id, 'step3')}
                        className="w-6 h-6 mt-1 text-indigo-600 rounded"
                        disabled={!steps.step2}
                      />
                      <div className="flex-1">
                        <h5 className={`text-lg font-bold mb-2 ${steps.step3 ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                          Post your review on Amazon
                        </h5>
                        {!steps.step3 && steps.step2 && (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-700">
                              Click the button below to post a review on Amazon.
                            </p>
                            <p className="text-sm text-gray-700">
                              Remember, we do not allow AI-written, plagiarized, generic, inaccurate, defamatory, or discriminatory reviews.
                            </p>
                            <p className="text-sm text-gray-700">
                              Amazon will not post your review immediately - that's ok! <strong>Continue on to step 4.</strong>
                            </p>
                            <div className="flex gap-3">
                              <a
                                href={adaptAmazonLink ? adaptAmazonLink(book.amazonLink, currentUser?.country) : book.amazonLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600 transition font-medium inline-flex items-center gap-2"
                              >
                                POST ON AMAZON
                                <ExternalLink className="w-4 h-4" />
                              </a>
                              <button className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-300 transition font-medium">
                                REPORT AN ISSUE
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Step 4: Submit review proof */}
                  <div className="border-2 border-gray-200 rounded-xl p-5">
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={steps.step4}
                        onChange={() => toggleStep(book.id, 'step4')}
                        className="w-6 h-6 mt-1 text-indigo-600 rounded"
                        disabled={!steps.step3}
                      />
                      <div className="flex-1">
                        <h5 className={`text-lg font-bold mb-2 ${steps.step4 ? 'line-through text-gray-400' : 'text-gray-800'}`}>
                          Submit your review proof
                        </h5>
                        {!steps.step4 && steps.step3 && (
                          <div className="space-y-3">
                            <p className="text-sm text-gray-700">
                              Submit proof that you posted your review on Amazon. Don't worry if your review hasn't appeared yet - it can take up to 46 days.
                            </p>
                            <button 
                              onClick={() => setSelectedBookForReview(book)}
                              className="bg-green-500 text-white px-6 py-2 rounded-lg hover:bg-green-600 transition font-medium"
                            >
                              SUBMIT REVIEW PROOF
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Book Summary (Expandable) */}
                  <div className="mt-6 border-t pt-6">
                    <button
                      onClick={() => setExpandedBook(isExpanded ? null : book.id)}
                      className="text-indigo-600 font-medium hover:text-indigo-800 transition"
                    >
                      {isExpanded ? '− Hide' : '+ Show'} Book Summary
                    </button>
                    {isExpanded && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                        <h4 className="font-bold text-gray-800 mb-2">Book Summary:</h4>
                        <p className="text-sm text-gray-700 whitespace-pre-wrap">
                          {book.fullSummary || book.description}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Review Submission Modal */}
      {selectedBookForReview && (
        <ReviewSubmissionModal
          book={selectedBookForReview}
          currentUser={currentUser}
          onClose={() => setSelectedBookForReview(null)}
          onSubmit={handleSubmitReview}
          AMAZON_MARKETPLACES={AMAZON_MARKETPLACES}
        />
      )}
    </div>
  );
};

export default ClaimedBooksWorkflow;